// SHADOW NETWORK — Share Post (additive).
// Generates a 1080x1080 composite image (chart photo + text panel + brand)
// from the analysis data, lets the user download it, share via Web Share API L2,
// or open intent URLs for X / Facebook / WhatsApp / Telegram.
//
// Pure client-side: uses HTML5 Canvas. No new deps required. Cloudinary serves
// the source image with permissive CORS, so we set crossOrigin="anonymous".
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Share2, Download, Check, X as XIcon, Loader2,
  Twitter, Facebook, MessageCircle, Send,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useHardcodedI18n } from "../i18n/hardcoded";
import { getShadowI18n } from "../i18n/shadow";
import { API } from "../lib/api";

const POSTER_W = 1080;
const POSTER_H = 1080;
const PHOTO_H = 648; // 60% of canvas
const BRAND_ROOT = "https://www.rebelbtc.com";

const DIRECTION_COLORS = {
  LONG: { fg: "#00e676", label: "↑ LONG" },
  SHORT: { fg: "#ff3355", label: "↓ SHORT" },
  NEUTRAL: { fg: "#94a3b8", label: "→ NEUTRAL" },
};

// Word-wrap a string onto a canvas given a max width in pixels.
function wrapText(ctx, text, maxWidth, maxLines) {
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const tentative = current ? current + " " + w : w;
    if (ctx.measureText(tentative).width > maxWidth && current) {
      lines.push(current);
      current = w;
      if (lines.length >= maxLines) break;
    } else {
      current = tentative;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  // Truncate last line with ellipsis if more text remains.
  if (lines.length === maxLines) {
    let last = lines[lines.length - 1];
    while (ctx.measureText(last + "…").width > maxWidth && last.length > 0) {
      last = last.slice(0, -1);
    }
    lines[lines.length - 1] = last + "…";
  }
  return lines;
}

function drawBadge(ctx, x, y, label, fg, bg, border) {
  ctx.save();
  ctx.font = "700 22px ui-monospace, 'JetBrains Mono', 'Courier New', monospace";
  const padX = 18;
  const padY = 10;
  const w = ctx.measureText(label).width + padX * 2;
  const h = 38;
  ctx.fillStyle = bg;
  ctx.strokeStyle = border;
  ctx.lineWidth = 1.5;
  // Sharp 2px rounded corners
  const r = 3;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = fg;
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + padX, y + h / 2 + 1);
  ctx.restore();
  return w + 12; // width consumed including gap
}

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Compose a 1080x1080 share poster onto the given canvas.
async function renderPoster(canvas, post) {
  const ctx = canvas.getContext("2d");
  canvas.width = POSTER_W;
  canvas.height = POSTER_H;

  // Background
  ctx.fillStyle = "#07080f";
  ctx.fillRect(0, 0, POSTER_W, POSTER_H);

  // --- Photo (top 60%) — cover fit ---
  try {
    const img = await loadImage(post.image_url);
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const scale = Math.max(POSTER_W / iw, PHOTO_H / ih);
    const sw = POSTER_W / scale;
    const sh = PHOTO_H / scale;
    const sx = (iw - sw) / 2;
    const sy = (ih - sh) / 2;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, POSTER_W, PHOTO_H);
  } catch {
    // Fallback gradient placeholder
    const g = ctx.createLinearGradient(0, 0, POSTER_W, PHOTO_H);
    g.addColorStop(0, "#1c2445");
    g.addColorStop(1, "#07080f");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, POSTER_W, PHOTO_H);
  }

  // Bottom gradient over photo for legibility
  const grad = ctx.createLinearGradient(0, PHOTO_H - 140, 0, PHOTO_H);
  grad.addColorStop(0, "rgba(7,8,15,0)");
  grad.addColorStop(1, "#07080f");
  ctx.fillStyle = grad;
  ctx.fillRect(0, PHOTO_H - 140, POSTER_W, 140);

  // --- Badges on photo (top-left) ---
  let bx = 24;
  const by = 22;
  if (post.ticker) {
    bx += drawBadge(ctx, bx, by, post.ticker, "#00d4ff", "rgba(0,212,255,0.16)", "rgba(0,212,255,0.45)");
  }
  if (post.timeframe) {
    bx += drawBadge(ctx, bx, by, post.timeframe, "#fbbf24", "rgba(251,191,36,0.16)", "rgba(251,191,36,0.45)");
  }
  if (post.direction && DIRECTION_COLORS[post.direction]) {
    const d = DIRECTION_COLORS[post.direction];
    bx += drawBadge(ctx, bx, by, d.label, d.fg, `${d.fg}24`, `${d.fg}80`);
  }

  // --- Text panel (bottom 40%) ---
  const PANEL_Y = PHOTO_H;
  const PANEL_PAD = 56;
  const TEXT_X = PANEL_PAD;
  const TEXT_W = POSTER_W - PANEL_PAD * 2;

  // Decorative cyan accent
  ctx.fillStyle = "#00d4ff";
  ctx.fillRect(TEXT_X, PANEL_Y + 28, 56, 3);

  // Title
  ctx.fillStyle = "#f0f4ff";
  ctx.textBaseline = "alphabetic";
  ctx.font = "900 42px Inter, ui-sans-serif, system-ui, sans-serif";
  const titleLines = wrapText(ctx, post.title || "", TEXT_W, 2);
  let y = PANEL_Y + 80;
  for (const line of titleLines) {
    ctx.fillText(line, TEXT_X, y);
    y += 52;
  }

  // Description
  ctx.fillStyle = "#c8d3e8";
  ctx.font = "400 24px Inter, ui-sans-serif, system-ui, sans-serif";
  const descMax = 5; // up to 5 lines, fits in panel
  const descLines = wrapText(ctx, post.description || "", TEXT_W, descMax);
  y += 16;
  for (const line of descLines) {
    ctx.fillText(line, TEXT_X, y);
    y += 36;
  }

  // --- Footer (author left, brand right) ---
  const FOOTER_Y = POSTER_H - 120;

  // Divider line
  ctx.fillStyle = "#1c2445";
  ctx.fillRect(TEXT_X, FOOTER_Y - 24, TEXT_W, 1);

  // Author avatar
  const avatarX = TEXT_X;
  const avatarY = FOOTER_Y - 4;
  const avatarR = 28;
  ctx.beginPath();
  ctx.arc(avatarX + avatarR, avatarY + avatarR, avatarR, 0, Math.PI * 2);
  ctx.fillStyle = "#00d4ff";
  ctx.fill();
  ctx.fillStyle = "#07080f";
  ctx.font = "800 26px Inter, ui-sans-serif, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(
    (post.user_name || "?").trim().charAt(0).toUpperCase(),
    avatarX + avatarR,
    avatarY + avatarR + 2
  );
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  // Author name + tag
  ctx.fillStyle = "#f0f4ff";
  ctx.font = "700 22px Inter, ui-sans-serif, system-ui, sans-serif";
  ctx.fillText(post.user_name || "anon", avatarX + avatarR * 2 + 14, avatarY + 22);
  ctx.fillStyle = "#6e7fa5";
  ctx.font = "600 16px ui-monospace, 'JetBrains Mono', 'Courier New', monospace";
  ctx.fillText("◈ SHADOW NETWORK™", avatarX + avatarR * 2 + 14, avatarY + 46);

  // Brand (right)
  const brandX = POSTER_W - PANEL_PAD;
  ctx.textAlign = "right";
  ctx.fillStyle = "#f0f4ff";
  ctx.font = "900 36px Inter, ui-sans-serif, system-ui, sans-serif";
  ctx.fillText("RE₿EL", brandX, avatarY + 26);
  ctx.fillStyle = "#6e7fa5";
  ctx.font = "600 16px ui-monospace, 'JetBrains Mono', 'Courier New', monospace";
  ctx.fillText("www.rebelbtc.com", brandX, avatarY + 50);
  ctx.textAlign = "left";
}

export const SharePost = ({ analysis }) => {
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const hc = useHardcodedI18n(lang);
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const blobRef = useRef(null);

  // Crawler-friendly share URL — backend renders OG meta tags at this path
  // and meta-refreshes humans into the SPA at /shadow/post/{id}. Without this
  // crawlers (X, FB, LI, Telegram) only see the static index.html OG defaults.
  //
  // Cache-bust ?v={timestamp} garantiza que X trate cada share como URL nueva
  // que nunca scrapeó → fuerza scrape fresco → obtiene la og:image real del
  // post en vez de una versión cacheada con la imagen genérica.
  const postUrl = `${BRAND_ROOT}/api/og/post/${analysis.id}?v=${Date.now()}`;
  const isVideoPost = analysis.media_type === "video";
  const shareText = isVideoPost
    ? `🎥 ${
        analysis.ticker
          ? `${analysis.ticker}${analysis.timeframe ? " · " + analysis.timeframe : ""} — `
          : ""
      }${analysis.title}\n\nVídeo análisis · ◈ SHADOW NETWORK™ — ${BRAND_ROOT.replace("https://", "")}`
    : `📊 ${
        analysis.ticker
          ? `${analysis.ticker}${analysis.timeframe ? " · " + analysis.timeframe : ""} — `
          : ""
      }${analysis.title}\n\n◈ SHADOW NETWORK™ — ${BRAND_ROOT.replace("https://", "")}`;

  // Generate the poster when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setGenerating(true);
    setError(null);
    setDownloaded(false);

    (async () => {
      try {
        const canvas = document.createElement("canvas");
        canvasRef.current = canvas;
        await renderPoster(canvas, analysis);
        if (cancelled) return;
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.92));
        blobRef.current = blob;
        if (blob && !cancelled) {
          setPreviewUrl(URL.createObjectURL(blob));
        }
      } catch (e) {
        if (!cancelled) setError(ui.sp_err_image);
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, analysis]);

  // Cleanup preview URL on close/unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleDownload = () => {
    if (!blobRef.current) return;
    const url = URL.createObjectURL(blobRef.current);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rebel-analysis-${analysis.id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1200);
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2400);
  };

  const handleNativeShare = async () => {
    if (!blobRef.current) return;
    try {
      const file = new File([blobRef.current], `rebel-analysis-${analysis.id}.png`, {
        type: "image/png",
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: analysis.title,
          text: shareText,
          url: postUrl,
          files: [file],
        });
        trackShare("native");
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: analysis.title, text: shareText, url: postUrl });
        trackShare("native");
      }
    } catch {
      /* user cancelled or unsupported — ignore */
    }
  };

  const intents = [
    {
      key: "x",
      label: "X",
      icon: Twitter,
      hover: "#1d9bf0",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(postUrl)}`,
    },
    {
      key: "fb",
      label: "Facebook",
      icon: Facebook,
      hover: "#1877f2",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`,
    },
    {
      key: "wa",
      label: "WhatsApp",
      icon: MessageCircle,
      hover: "#25d366",
      url: `https://wa.me/?text=${encodeURIComponent(shareText + "\n" + postUrl)}`,
    },
    {
      key: "tg",
      label: "Telegram",
      icon: Send,
      hover: "#0088cc",
      url: `https://t.me/share/url?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(shareText)}`,
    },
  ];

  const canNativeShare = typeof navigator !== "undefined" && (navigator.share || navigator.canShare);

  // Fire-and-forget share tracking. Backend rate-limits por IP+platform 60s,
  // así que múltiples clicks accidentales sólo cuentan 1x.
  const trackShare = (platform) => {
    try {
      fetch(`${API}/shadow/social/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analysis_id: analysis.id, platform }),
        keepalive: true,
      }).catch(() => { /* ignore */ });
    } catch { /* ignore */ }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-white/10 px-1.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white/55 transition-colors hover:border-cyan-400/40 hover:text-cyan-200 sm:px-2"
        data-testid={`shadow-card-share-${analysis.id}`}
        aria-label={ui.sp_aria}
        title="Compartir"
      >
        <Share2 className="h-3 w-3" />
        <span className="hidden sm:inline">{ui.sp_btn}</span>
      </button>

      {ReactDOM.createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9200] flex items-start justify-center overflow-y-auto bg-black/75 p-3 backdrop-blur-md sm:items-center sm:p-4"
              onClick={() => setOpen(false)}
              data-testid="share-post-modal"
            >
            <motion.div
              initial={{ scale: 0.96, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 10, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative my-6 w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 text-white shadow-[0_0_60px_rgba(0,212,255,0.18)]"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute right-3 top-3 z-10 rounded-full border border-white/15 bg-black/70 p-1.5 text-white/70 backdrop-blur hover:text-white"
                aria-label="cerrar"
                data-testid="share-post-close"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>

              <div className="p-5 sm:p-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300/80">
                  ◈ Shadow Network · Compartir
                </p>
                <h3 className="mt-1 text-lg font-black tracking-tight">{hc.share_this_analysis}</h3>
                <p className="mt-1 text-[11px] text-white/45">
                  {hc.share_image_spec}
                </p>

                {/* PREVIEW */}
                <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-black">
                  {generating ? (
                    <div className="flex aspect-square items-center justify-center text-white/50">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span className="text-xs">{hc.share_generating_image}</span>
                    </div>
                  ) : error ? (
                    <div className="flex aspect-square items-center justify-center px-6 text-center text-xs text-rose-300">
                      {error}
                    </div>
                  ) : previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="vista previa"
                      className="aspect-square w-full object-cover"
                      data-testid="share-post-preview"
                    />
                  ) : null}
                </div>

                {/* DOWNLOAD */}
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={!blobRef.current || generating}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-400/50 bg-cyan-400/[0.08] px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-100 transition-colors hover:bg-cyan-400/[0.16] disabled:opacity-50"
                  data-testid="share-post-download"
                >
                  {downloaded ? (
                    <>
                      <Check className="h-3.5 w-3.5" /> Imagen descargada
                    </>
                  ) : generating ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generando…
                    </>
                  ) : (
                    <>
                      <Download className="h-3.5 w-3.5" /> Descargar imagen completa
                    </>
                  )}
                </button>
                <p className="mt-1.5 text-center text-[10px] text-white/35">
                  Súbela a Instagram, Stories de Facebook o X
                </p>

                {/* NATIVE SHARE (mobile) */}
                {canNativeShare && (
                  <button
                    type="button"
                    onClick={handleNativeShare}
                    disabled={!blobRef.current || generating}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.18em] text-white/80 transition-colors hover:bg-white/[0.08] disabled:opacity-50"
                    data-testid="share-post-native"
                  >
                    <Share2 className="h-3.5 w-3.5" /> Compartir con apps
                  </button>
                )}

                {/* SOCIAL INTENTS */}
                <p className="mt-5 mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
                  o comparte el enlace directamente
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {intents.map((it) => (
                    <a
                      key={it.key}
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackShare(it.key === "fb" ? "facebook" : it.key === "wa" ? "whatsapp" : it.key === "tg" ? "telegram" : it.key)}
                      className="group inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-white/65 transition-all"
                      style={{
                        "--hover-color": it.hover,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = it.hover;
                        e.currentTarget.style.color = it.hover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "";
                        e.currentTarget.style.color = "";
                      }}
                      data-testid={`share-post-${it.key}-${analysis.id}`}
                    >
                      <it.icon className="h-3.5 w-3.5" />
                      {it.label}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default SharePost;
